import { useMemo } from "react";
import {
  addDays,
  format,
  isToday,
  differenceInMinutes,
  parseISO,
} from "date-fns";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "@/hooks/useCalendar";
import type { Item } from "@/lib/types";

const START_HOUR = 7;
const END_HOUR = 22;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const HOUR_HEIGHT = 48; // px per hour

type Props = {
  currentWeekStart: Date;
  events: CalendarEvent[];
  items: Item[];
  onSelectDate: (date: Date) => void;
  onTimeSlotClick: (date: Date, hour: number) => void;
  onTaskClick: (item: Item) => void;
  onEventClick: (event: CalendarEvent) => void;
};

export function WeekTimeline({
  currentWeekStart,
  events,
  items,
  onSelectDate,
  onTimeSlotClick,
  onTaskClick,
  onEventClick,
}: Props) {
  const days = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < 7; i++) {
      result.push(addDays(currentWeekStart, i));
    }
    return result;
  }, [currentWeekStart]);

  // Group events by day
  const eventsByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const day of days) {
      map[format(day, "yyyy-MM-dd")] = [];
    }
    for (const event of events) {
      if (event.all_day) continue;
      const dateStr = event.start.slice(0, 10);
      if (map[dateStr]) {
        map[dateStr].push(event);
      }
    }
    return map;
  }, [events, days]);

  // Group all-day events by day
  const allDayByDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const day of days) {
      map[format(day, "yyyy-MM-dd")] = [];
    }
    for (const event of events) {
      if (!event.all_day) continue;
      const dateStr = event.start.slice(0, 10);
      if (map[dateStr]) {
        map[dateStr].push(event);
      }
    }
    return map;
  }, [events, days]);

  // Group items (tasks) by day
  const itemsByDay = useMemo(() => {
    const map: Record<string, Item[]> = {};
    for (const day of days) {
      map[format(day, "yyyy-MM-dd")] = [];
    }
    for (const item of items) {
      if (!item.date_target) continue;
      if (map[item.date_target]) {
        map[item.date_target].push(item);
      }
    }
    return map;
  }, [items, days]);

  const hours = useMemo(() => {
    const result: number[] = [];
    for (let h = START_HOUR; h < END_HOUR; h++) {
      result.push(h);
    }
    return result;
  }, []);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Day headers */}
        <div className="grid grid-cols-[3.5rem_repeat(7,1fr)] border-b border-border sticky top-0 bg-background z-10">
          <div /> {/* spacer for time gutter */}
          {days.map((day) => {
            const today = isToday(day);
            const dateStr = format(day, "yyyy-MM-dd");
            return (
              <button
                key={dateStr}
                onClick={() => onSelectDate(day)}
                className={cn(
                  "text-center py-2 border-l border-border hover:bg-muted/50 transition-colors",
                  today && "bg-primary/5"
                )}
              >
                <div className="text-[10px] uppercase text-muted-foreground">
                  {format(day, "EEE")}
                </div>
                <div
                  className={cn(
                    "text-sm font-medium mt-0.5 inline-flex items-center justify-center",
                    today &&
                      "bg-primary text-primary-foreground rounded-full w-7 h-7"
                  )}
                >
                  {format(day, "d")}
                </div>
              </button>
            );
          })}
        </div>

        {/* All-day / tasks header row */}
        <div className="grid grid-cols-[3.5rem_repeat(7,1fr)] border-b border-border">
          <div className="text-[10px] text-muted-foreground text-right pr-2 py-1">
            All day
          </div>
          {days.map((day) => {
            const dateStr = format(day, "yyyy-MM-dd");
            const allDay = allDayByDay[dateStr] || [];
            const dayTasks = itemsByDay[dateStr] || [];
            return (
              <div
                key={dateStr}
                className="border-l border-border p-1 min-h-[2rem] space-y-0.5"
              >
                {allDay.map((evt) => (
                  <button
                    key={evt.id}
                    onClick={() => onEventClick(evt)}
                    className="block w-full text-left text-[10px] leading-tight truncate rounded px-1 py-0.5 bg-blue-500/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25"
                  >
                    {evt.title}
                  </button>
                ))}
                {dayTasks.slice(0, 2).map((task) => (
                  <button
                    key={task.id}
                    onClick={() => onTaskClick(task)}
                    className="block w-full text-left text-[10px] leading-tight truncate rounded px-1 py-0.5 bg-primary/10 text-primary hover:bg-primary/20"
                  >
                    {task.title}
                  </button>
                ))}
                {dayTasks.length > 2 && (
                  <span className="text-[10px] text-muted-foreground px-1">
                    +{dayTasks.length - 2}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Hourly grid */}
        <div className="relative grid grid-cols-[3.5rem_repeat(7,1fr)]">
          {/* Time labels */}
          <div className="relative">
            {hours.map((h) => (
              <div
                key={h}
                className="text-[10px] text-muted-foreground text-right pr-2 border-b border-border"
                style={{ height: HOUR_HEIGHT }}
              >
                {format(new Date(2000, 0, 1, h), "h a")}
              </div>
            ))}
          </div>

          {/* Day columns with events */}
          {days.map((day) => {
            const dateStr = format(day, "yyyy-MM-dd");
            const dayEvents = eventsByDay[dateStr] || [];
            const today = isToday(day);

            return (
              <div
                key={dateStr}
                className={cn(
                  "relative border-l border-border",
                  today && "bg-primary/[0.02]"
                )}
              >
                {/* Hour slots (clickable) */}
                {hours.map((h) => (
                  <button
                    key={h}
                    onClick={() => onTimeSlotClick(day, h)}
                    className="w-full border-b border-border hover:bg-muted/30 transition-colors"
                    style={{ height: HOUR_HEIGHT }}
                  />
                ))}

                {/* Event blocks */}
                {dayEvents.map((event) => {
                  const eventStart = parseISO(event.start);
                  const eventEnd = parseISO(event.end);
                  const startMinutes =
                    eventStart.getHours() * 60 + eventStart.getMinutes();
                  const duration = differenceInMinutes(eventEnd, eventStart);

                  const topPx =
                    ((startMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT;
                  const heightPx = Math.max(
                    (duration / 60) * HOUR_HEIGHT,
                    20
                  );

                  // Clamp to grid bounds
                  if (topPx + heightPx <= 0 || topPx >= TOTAL_HOURS * HOUR_HEIGHT) {
                    return null;
                  }

                  return (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className="absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 bg-blue-500/20 border-l-2 border-blue-500 hover:bg-blue-500/30 transition-colors overflow-hidden text-left"
                      style={{
                        top: Math.max(topPx, 0),
                        height: heightPx,
                      }}
                    >
                      <div className="text-[10px] font-medium text-blue-700 dark:text-blue-300 truncate">
                        {event.title}
                      </div>
                      {heightPx > 30 && (
                        <div className="text-[9px] text-blue-600/70 dark:text-blue-400/70">
                          {format(eventStart, "h:mm a")}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
